/**
 * #409 / #410 — the ONE recipients sort-key contract.
 *
 * `GET /v1/recipients` accepts exactly the twelve keys below — `RECIPIENTS_SORT_KEYS`
 * in `apps/api/src/modules/recipients/dto/filter-recipients.dto.ts`, enforced by
 * `@IsIn`, so anything else is a 400 BadRequest, not a silently ignored sort.
 *
 * The table advertises ~23 sortable column ids. There used to be no mapping
 * table at all — just two one-off ternaries (`"lists" → groupCount` in the page
 * hook, `"name" → firstName` in the query hook), so every OTHER header click
 * sent the raw column id and the list 400'd. Worse (#410), the rejected key was
 * PERSISTED: into `recipients-table-sorting` (localStorage) and from there into
 * the `view-pref:{userId}:{workspaceId}` Redis row on a 180-day sliding TTL — so
 * one click broke that user's recipients list in every browser, and the empty
 * result fell through to the first-run "No recipients yet" screen.
 *
 * Both halves now resolve through {@link toServerSortKey}: it returns the API
 * key, or `null` for a column the API cannot order by — and `null` means "send
 * no sort and persist no sort", never "send the id and hope".
 */

/** Mirror of RECIPIENTS_SORT_KEYS on the API DTO. Keep the two in step. */
export const RECIPIENTS_SORT_KEYS = [
  'createdAt',
  'updatedAt',
  'email',
  'firstName',
  'lastName',
  'department',
  'designation',
  'location',
  'domain',
  'status',
  'profileCompleteness',
  'groupCount',
] as const;

export type RecipientsSortKey = (typeof RECIPIENTS_SORT_KEYS)[number];

const SERVER_SORT_KEYS: ReadonlySet<string> = new Set(RECIPIENTS_SORT_KEYS);

/** True when `value` is one of the keys the API accepts. */
export function isRecipientsSortKey(
  value: string | null | undefined,
): value is RecipientsSortKey {
  return !!value && SERVER_SORT_KEYS.has(value);
}

/**
 * Every column id `createRecipientsColumns()` can produce → the API sort key it
 * orders by, or `null` where the backend has no ordering for it (a computed
 * cell, a joined label, a JSONB custom field). `null` is a DECISION, not an
 * omission: `lib/__tests__/recipients-sort-keys.guard.spec.ts` fails if a new
 * column arrives without an entry here.
 */
export const RECIPIENT_COLUMN_SORT_KEYS: Readonly<
  Record<string, RecipientsSortKey | null>
> = {
  // ── server-sortable ──────────────────────────────────────────────────────
  /** display column composed of firstName + lastName */
  name: 'firstName',
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  department: 'department',
  /** owner, 2026-10-14 — the API puts blank values last in both directions */
  designation: 'designation',
  location: 'location',
  /** production #1906 — the API orders by the domain the column shows; blanks last both ways */
  domain: 'domain',
  status: 'status',
  /** #167 — the Groups column's accessorKey; the API sorts by the group COUNT */
  lists: 'groupCount',
  /** the completeness BAR; the API computes the same 8-field score */
  completeness: 'profileCompleteness',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',

  // ── not server-sortable (see the header note) ────────────────────────────
  select: null,
  actions: null,
  person: null,
  upn: null,
  phoneNumber: null,
  country: null,
  tags: null,
  courseCount: null,
  source: null,
  manager: null,
  employmentType: null,
  isRemote: null,
  /** a Date CUSTOM FIELD (JSONB) — filterable via cfDate*, not orderable */
  hireDate: null,
};

/**
 * Column id (or an API key already — the `?sortBy` URL param carries API keys,
 * not column ids) → the key to send the API, or `null` when the API cannot sort
 * by it. Custom-field columns (`cf_<key>`) fall through to `null`.
 */
export function toServerSortKey(
  columnId: string | null | undefined,
): RecipientsSortKey | null {
  if (!columnId) return null;
  if (isRecipientsSortKey(columnId)) return columnId;
  return RECIPIENT_COLUMN_SORT_KEYS[columnId] ?? null;
}

/** True when a header click on this column can produce a real server sort. */
export function isServerSortableColumn(columnId: string): boolean {
  return toServerSortKey(columnId) !== null;
}

/**
 * Inverse of {@link toServerSortKey}: the column whose header arrow represents
 * an API sort key. Only the two keys that are NOT their own column id need
 * translating; the rest are identity.
 */
const SORT_KEY_TO_COLUMN_ID: Readonly<Record<RecipientsSortKey, string>> = {
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  email: 'email',
  firstName: 'firstName',
  lastName: 'lastName',
  department: 'department',
  designation: 'designation',
  location: 'location',
  domain: 'domain',
  status: 'status',
  profileCompleteness: 'completeness',
  groupCount: 'lists',
};

export function toColumnSortId(sortKey: string): string {
  return isRecipientsSortKey(sortKey) ? SORT_KEY_TO_COLUMN_ID[sortKey] : sortKey;
}

/**
 * Stop a column advertising a sort the server cannot perform. The recipients
 * table is server-paginated, so a header click on an unmappable column could
 * only ever re-order the 25 rows already on screen — while poisoning the saved
 * view (#410). Applied where the columns are built, so `enableSorting` and the
 * key map can never disagree.
 */
export function withServerSortableColumns<
  T extends {
    id?: string;
    accessorKey?: unknown;
    enableSorting?: boolean;
    sortingFn?: unknown;
    sortUndefined?: unknown;
  },
>(columns: T[]): T[] {
  return columns.map((column) => {
    const columnId = column.id ?? String(column.accessorKey ?? '');
    if (column.enableSorting === false) return column;
    if (isServerSortableColumn(columnId)) {
      // A column that brings its own comparator keeps it: Groups (#167) counts
      // the same groups the server's groupCount counts, so it agrees.
      return column.sortingFn
        ? column
        : { ...column, sortingFn: keepServerOrder, sortUndefined: false };
    }
    return { ...column, enableSorting: false };
  });
}

/**
 * B3 (table audit, 14 Oct 2026) — the rows arrive already sorted by the API.
 * All Recipients sends its sort through the `recipients-sort-change` event, not
 * the table's `onSortChange`, so the table cannot tell the server owns the
 * order (`manualSorting` stays false) and sorted each page of 25 again with its
 * own text rule: Email Z→A moved qa.zelda… from the server's row 24 to row 2.
 * Calling every pair equal (and not moving empty values, `sortUndefined: false`)
 * leaves TanStack's tie-break, the row's original position: the server's order.
 */
function keepServerOrder(): number {
  return 0;
}
